/**
 * The offset.
 */
export interface Offset {
  /**
   * The total vertical offset in pixels.
   */
  readonly top: number;

  /**
   * The total horizontal offset in pixels.
   */
  readonly left: number;
}

/**
 * Gets the positions of two elements relative to each other.
 * @param firstElement The first element.
 * @param secondElement The second element.
 * @returns The offset in pixels between the first and second elements.
 */
function getElementRelativePositions(firstElement: HTMLElement, secondElement: HTMLElement): Offset {
  const { top: firstTop, left: firstLeft } = firstElement.getBoundingClientRect();
  const { top: secondTop, left: secondLeft } = secondElement.getBoundingClientRect();

  return {
    top: firstTop - secondTop,
    left: firstLeft - secondLeft,
  };
}

/**
 * Gets the relative offset between the element and the specified parent. If the parent is never found when searching, the offset
 * will be relative to the viewport.
 * @param element The element to get the offset for.
 * @param parent The parent of the element to get the offset until.
 * @returns The offset between the element and its parent.
 */
export function getOffsetRelativeTo(element: HTMLElement, parent: HTMLElement): Offset {
  let top = 0;
  let left = 0;
  let currentElement = element;

  while (currentElement && currentElement !== parent) {
    const offsetParent = currentElement.offsetParent as HTMLElement;

    if (offsetParent && offsetParent instanceof HTMLElement) {
      const { top: offsetTop, left: offsetLeft } = getElementRelativePositions(currentElement, offsetParent);
      top += offsetTop;
      left += offsetLeft;
    }

    currentElement = offsetParent;
  }

  return { top, left };
}
